import {
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { Stack } from "@/core/components/ui/stack";
import type { ApPurchaseDocument } from "@/accounts-payable/types";
import { useApInvoiceDocument } from "@/accounts-payable/use-invoice-document";
import { documentLabel } from "@/accounts-payable/utils";

/** The document preview for any purchase document's review page. */
export const DocumentReviewPreview = ({
  document: purchaseDocument,
}: {
  document: ApPurchaseDocument;
}) => {
  const kind = purchaseDocument.document_type ?? "invoice";
  const label = documentLabel(purchaseDocument);
  const {
    objectUrl: documentUrl,
    isLoading: documentLoading,
    isError: documentError,
  } = useApInvoiceDocument(purchaseDocument.file_url);

  return (
    <Stack gap="sm" fullWidth className="self-start lg:sticky lg:top-4">
      <Stack
        gap="none"
        fullWidth
        className="overflow-hidden rounded-lg border border-stroke-tertiary bg-card"
      >
        {/* Toolbar */}
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          fullWidth
          className="flex-none border-b border-stroke-tertiary bg-card px-4 py-2.5"
        >
          <Stack direction="row" align="center" gap="sm" className="min-w-0">
            <DocumentTextIcon className="h-4 w-4 flex-none text-tertiary-text" />
            <span className="truncate text-md font-semibold">
              {purchaseDocument.attachment?.file_name ??
                `${purchaseDocument.document_number ?? kind}.pdf`}
            </span>
          </Stack>
          {documentUrl && (
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-none items-center gap-1 text-md font-semibold text-interactive-text hover:underline"
            >
              Open
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </a>
          )}
        </Stack>

        {/* Body */}
        {purchaseDocument?.file_url ? (
          documentError ? (
            <Stack
              align="center"
              justify="center"
              gap="xs"
              fullWidth
              className="h-[40vh] bg-bg-secondary/40 text-center"
            >
              <DocumentTextIcon className="h-8 w-8 text-quaternary-text" />
              <span className="text-tertiary-text">
                Couldn’t load the {kind} document.
              </span>
            </Stack>
          ) : !documentUrl || documentLoading ? (
            <Stack
              align="center"
              justify="center"
              gap="xs"
              fullWidth
              className="h-[40vh] bg-bg-secondary/40 text-center"
            >
              <span className="text-tertiary-text">Loading document…</span>
            </Stack>
          ) : purchaseDocument.file_content_type?.startsWith("image/") ? (
            <Stack
              gap="none"
              fullWidth
              className="max-h-[78vh] overflow-auto bg-bg-secondary/40 p-4"
            >
              <img
                src={documentUrl}
                alt={label}
                className="mx-auto w-full max-w-[820px] rounded-md shadow-sm"
              />
            </Stack>
          ) : (
            <iframe
              // Hide the browser's native PDF chrome and fit to width for a
              // cleaner embedded look.
              src={`${documentUrl}#toolbar=0&navpanes=0&view=FitH`}
              title={label}
              className="h-[78vh] w-full border-0 bg-bg-secondary/40"
            />
          )
        ) : (
          <Stack
            align="center"
            justify="center"
            gap="xs"
            fullWidth
            className="h-[40vh] bg-bg-secondary/40 text-center"
          >
            <DocumentTextIcon className="h-8 w-8 text-quaternary-text" />
            <span className="text-tertiary-text">
              No document available for this {kind}.
            </span>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
